import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Text, PeopleSearchSelect } from '@evertel/web/ui'
import Wrapper from './Wrapper'
import { UserTile } from '@evertel/web/user'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataBlueUser } from '@evertel/types'
import { useService } from '@evertel/di'
import { UserWidgetController } from '@evertel/blue-user'
import { MultiValue, SingleValue } from 'react-select'
import { formatForSelect } from '@evertel/utils'

export interface UserWriteWidgetProps {
    id: string
    htmlId?: string
    value: number
    onChange: (id: string, value: number) => void
    title: string
    required?: boolean
    scope?: string
    errors?: string
    documentId?: number
    hint?: string
    placeholder?: string
    description?: string
    label?: string
}

const UserWriteWidget: React.FC<UserWriteWidgetProps> = observer(({
    id,
    htmlId,
    value: userId,
    onChange,
    title,
    placeholder,
    required,
    scope,
    errors,
    documentId,
    ...otherProps
}) => {

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`
    const hasErrors = errors && errors.length > 0

    const sessionService = useService(SessionService, [])
    const userWidgetController = useService(UserWidgetController, [])

    useEffect(() => {
        userWidgetController.init(
            userId,
            documentId
        )
    }, [userId, documentId])

    const _onChange = (user: MultiValue<APIDataBlueUser> | SingleValue<APIDataBlueUser>) => {
        user = Array.isArray(user) ? user[0] : user

        if (onChange && scope !== 'schemaBuilder') {
            const _user = user as APIDataBlueUser
            const _userId = _user?.id as number
            onChange(id, _userId)
        }
    }

    const selectedUser = (userId && userWidgetController.user) ? formatForSelect([userWidgetController.user], 'firstName+lastName', 'id', undefined, 'email') : []

    return (
        <Wrapper
            title={title}
            required={required}
            errors={errors}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            <PeopleSearchSelect
                inputId={inputId}
                searchScope="department"
                departmentId={sessionService?.selectedDepartmentId}
                selected={selectedUser as APIDataBlueUser[]}
                onSelect={_onChange}

                placeholder={placeholder || 'Search or select people...'}
                isClearable
                showPosition
                menuPlacement="auto"
                forceShowDepartment

                isDisabled={scope === 'schemaBuilder'}

                aria-labelledby={title || otherProps.hint ? labelId : undefined}
                aria-invalid={hasErrors ? true : undefined}
                aria-errormessage={hasErrors ? errorId : undefined}
                aria-required={required ? true : undefined}
            />
        </Wrapper>
    )
})

// ----------------------------------------------------------------------------------------

export interface UserReadWidgetProps {
    htmlId: string
    value: number
    title?: string
    description?: string
    hint?: string
    documentId?: number
}

const UserReadWidget: React.FC<UserReadWidgetProps> = observer(({
    htmlId,
    value: userId,
    title,
    description,
    hint,
    documentId,
    ...otherProps
}) => {

    const userWidgetController = useService(UserWidgetController, [])

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    useEffect(() => {
        userWidgetController.init(
            userId,
            documentId
        )
    }, [userId, documentId])


    return (
        <Wrapper
            title={title}
            description={description}
            hint={hint}
            labelId={labelId}
            descriptionId={descriptionId}
            {...otherProps}
        >
            {(userId) ?
                <UserTile
                    id={inputId}
                    userId={userId}
                />
                :
                <Text id={inputId}>
                    <>
                        <span className="visually-hidden">No value</span>
                        <span aria-hidden="true">---</span>
                    </>
                </Text>
            }
        </Wrapper>
    )
})

export {
    UserWriteWidget,
    UserReadWidget
}
